import constants from '../qti/Constants';
import settingsReader from '../store/readHelpers/SettingsReader';
import excalidrawPreloadService from './ExcalidrawPreloadService';
import utilService from './UtilService';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import storeManager from '../store/StoreManager';
import responseService from './ResponseService';
import questionHelperServices from './questionView/QuestionHelperServices';
import sessionConstants from '../constants/SessionConstants';
import isDrawingResponseIdentifierUtil from './DrawingUtils';

class DrawingCanvasService {
    static isDrawingEnabled(question) {
        const enabled = settingsReader.isCandidateDrawingOptionEnabled() &&
               question?.responseModels?.some(model => model.type === constants.interactionType.DRAWING_INTERACTION);
        return !!enabled;
    }


    static getDrawingConfig(question) {
        if (!this.isDrawingEnabled(question)) {
            return { enabled: false };
        }

        const drawingInteraction = question.responseModels.find(model =>
            model.type === constants.interactionType.DRAWING_INTERACTION
        );

        const config = {
            enabled: true,
            allowOrientationOverride: drawingInteraction.allowOrientationOverride || false,
            maxHeight: drawingInteraction.maxHeight,
            maxWidth: drawingInteraction.maxWidth,
            responseIdentifier: drawingInteraction.responseIdentifier,
        };

        return config;
    }


    static createExtraResponses(responseIdentifier, fileInfo, options = {}) {
        const { jsonId = '', fileName = '', fileType = '', fileSize = '' } = fileInfo;
        const { offlineData = null, imageS3Uri = null, jsonS3Uri = null } = options;

        const extraResponses = [
            { identifier: `${responseIdentifier}_jsonId`, candidateResponse: [jsonId] },
            { identifier: `${responseIdentifier}_fileName`, candidateResponse: [fileName] },
            { identifier: `${responseIdentifier}_fileType`, candidateResponse: [fileType] },
            { identifier: `${responseIdentifier}_fileSize`, candidateResponse: [fileSize] },
        ];

        if (settingsReader.shouldStoreDrawingsAsUri()) {
            if (imageS3Uri) {
                extraResponses.push({
                    identifier: `${responseIdentifier}_fileURI`,
                    candidateResponse: [imageS3Uri],
                });
            }
            if (jsonS3Uri) {
                extraResponses.push({
                    identifier: `${responseIdentifier}_jsonDataURI`,
                    candidateResponse: [jsonS3Uri],
                });
            }
        }

        if (offlineData?.imageBase64 && offlineData?.sceneJson) {
            extraResponses.push(
                {
                    identifier: `${responseIdentifier}_drawingBase64`,
                    candidateResponse: [offlineData.imageBase64],
                },
                {
                    identifier: `${responseIdentifier}_jsonData`,
                    candidateResponse: [offlineData.sceneJson],
                },
                {
                    identifier: `${responseIdentifier}_pendingSync`,
                    candidateResponse: ['false'],
                }
            );
        }

        return extraResponses;
    }


    static getDrawingExtraIdentifiers(responseIdentifier) {
        return [
            `${responseIdentifier}_jsonId`,
            `${responseIdentifier}_fileName`,
            `${responseIdentifier}_fileType`,
            `${responseIdentifier}_fileSize`,
            `${responseIdentifier}_drawingBase64`,
            `${responseIdentifier}_jsonData`,
            `${responseIdentifier}_pendingSync`,
            `${responseIdentifier}_fileURI`,
            `${responseIdentifier}_jsonDataURI`,
        ];
    }


    static isDrawingResponseIdentifier(identifier) {
        return isDrawingResponseIdentifierUtil(identifier);
    }

    static getExtraResponses(userResponses, drawingConfig) {
        if (!userResponses || !drawingConfig?.responseIdentifier) {
            return [];
        }

        if (!drawingConfig.enabled) {
            return [];
        }

        const drawingExtraIdentifiers = this.getDrawingExtraIdentifiers(drawingConfig.responseIdentifier);

        const extraResponses = drawingExtraIdentifiers
            .filter(identifier => userResponses[identifier] && userResponses[identifier].length > 0)
            .map(identifier => ({
                identifier,
                userResponse: userResponses[identifier] || [],
            }));

        return extraResponses;
    }

    static hasExistingDrawing(userResponses, drawingConfig, extraResponses) {
        if (!drawingConfig?.enabled) {
            return false;
        }

        const drawingResponse = userResponses?.[drawingConfig?.responseIdentifier];
        const hasExistingDrawing = drawingResponse && drawingResponse.length > 0 && !!drawingResponse[0];
        const hasDrawingExtraResponses = (() => {
            if (!extraResponses || extraResponses.length === 0) return false;
            const fileNameObj = extraResponses.find(
                r => r.identifier === 'DRAWING-RESPONSE_fileName' || r.identifier === 'DRAWING-RESPONSE_drawingBase64'
            );
            const hasFileName =
            fileNameObj &&
            Array.isArray(fileNameObj.userResponse) &&
            fileNameObj.userResponse[0];

            return !!hasFileName;
        })();

        const result = hasExistingDrawing || hasDrawingExtraResponses;

        return result;
    }

    static sanitizeElement(element = {}) {
        if (!element || element.isDeleted) {
            return null;
        }
        const {
            version,
            versionNonce,
            updated,
            updatedBy,
            boundElements,
            link,
            locked,
            ...rest
        } = element;

        return rest;
    }

    static sanitizeAppState(appState = {}) {
        const volatileKeys = new Set([
            'scrollX', 'scrollY', 'zoom', 'offsetLeft', 'offsetTop',
            'editingGroupId', 'draggingElement', 'resizingElement', 'multiElement',
            'selectionElement', 'pendingImageElementId', 'editingLinearElement',
            'hoveredElement', 'selectedElementIds', 'selectedGroupIds',
            'toastMessage', 'elementType', 'exportBackground', 'pendingFileUploads',
        ]);

        return Object.keys(appState).reduce((sanitized, key) => {
            if (!volatileKeys.has(key)) {
                sanitized[key] = appState[key];
            }
            return sanitized;
        }, {});
    }

    static sanitizeFiles(files = {}) {
        return Object.keys(files || {})
            .sort((a, b) => a.localeCompare(b))
            .map((id) => {
                const file = files[id];
                if (!file) return null;
                const { created, lastRetrieved, ...sanitizedFile } = file;
                return { id, ...sanitizedFile };
            })
            .filter(Boolean);
    }

    static captureSnapshot(excalidrawRef) {
        if (!excalidrawRef?.current) {
            return null;
        }
        const elements = excalidrawRef.current.getSceneElements?.() || [];
        const appState = excalidrawRef.current.getAppState?.() || {};
        const files = excalidrawRef.current.getFiles?.() || {};

        // Check if canvas is empty (no elements or all deleted)
        const hasValidElements = elements.some(el => el && !el.isDeleted);
        if (!hasValidElements) {
            return null;
        }

        // Sanitize data to remove volatile fields for comparison
        const sanitizedElements = (elements || [])
            .map(this.sanitizeElement)
            .filter(Boolean)
            .sort((a, b) => (a?.id || '').localeCompare(b?.id || ''));
        const sanitizedAppState = this.sanitizeAppState(appState || {});
        const sanitizedFiles = this.sanitizeFiles(files || {});

        const serializeFn = excalidrawPreloadService.getSerializeAsJSON();
        if (typeof serializeFn === 'function') {
            return serializeFn(sanitizedElements, sanitizedAppState, sanitizedFiles);
        }

        return JSON.stringify({
            elements: sanitizedElements,
            appState: sanitizedAppState,
            files: sanitizedFiles,
        });
    }

    static createImageBlob(imageData, fileName) {
        const byteString = atob(imageData.split(',')[1]);
        const mimeString = imageData.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        const imageBlob = new Blob([ab], { type: mimeString });
        imageBlob.name = fileName;
        return { blob: imageBlob, mimeType: mimeString };
    }

    static createJsonBlob(scene, jsonFileName) {
        const jsonString = JSON.stringify(scene);
        const jsonBlob = new Blob([jsonString], { type: 'application/json' });
        jsonBlob.name = jsonFileName;
        return jsonBlob;
    }


    static async loadDrawingDataFromUrl(jsonId, services) {
        if (!jsonId || !services?.uploadService?.getFileDownloadUrl) {
            return null;
        }

        const jsonUrl = services.uploadService.getFileDownloadUrl(jsonId);

        try {
            const response = await fetch(jsonUrl);
            if (!response.ok) {
                return null;
            }
            const data = await response.json();
            return data.elements || data;
        } catch (err) {
            console.error('Failed to load drawing data:', err);
            return null;
        }
    }

    static getDrawingImageUrlFromId(imageId, services) {
        if (!imageId || !services?.uploadService?.getFileDownloadUrl) {
            return null;
        }
        return services.uploadService.getFileDownloadUrl(imageId);
    }

    static async exportDrawingToImage(excalidrawRef) {
        if (!excalidrawRef?.current?.getSceneElements || !excalidrawRef?.current?.getAppState) {
            return null;
        }

        const elements = excalidrawRef.current.getSceneElements();
        const appState = excalidrawRef.current.getAppState();
        const files = excalidrawRef.current.getFiles ? excalidrawRef.current.getFiles() : undefined;

        await excalidrawPreloadService.getExcalidrawComponent();
        const exportToCanvas = excalidrawPreloadService.getExportToCanvas();

        const imageData = await exportToCanvas({
            elements,
            appState: {
                ...appState,
                exportBackground: true,
            },
            files,
        });

        if (!imageData) {
            return null;
        }

        return {
            imageData,
            elements,
            appState,
            files,
            finalImageData: imageData.toDataURL('image/png'),
        };
    }

    static createOfflineExtraResponses(responseIdentifier, imageData, sceneJson) {
        return [
            {
                identifier: `${responseIdentifier}_drawingBase64`,
                candidateResponse: [imageData],
            },
            {
                identifier: `${responseIdentifier}_jsonData`,
                candidateResponse: [sceneJson],
            },
            {
                identifier: `${responseIdentifier}_pendingSync`,
                candidateResponse: ['true'],
            },
        ];
    }

    static async saveDrawingToServer(
        excalidrawRef,
        services,
        question,
        drawingConfig,
        isDarkModeEnabled,
        handleUserResponse
    ) {
        if (!excalidrawRef.current || !services || !question) {
            throw new Error('Missing required parameters for saving drawing');
        }

        const exportResult = await this.exportDrawingToImage(excalidrawRef);
        if (!exportResult) {
            throw new Error('Failed to export drawing');
        }

        const { finalImageData, elements, appState, files } = exportResult;

        if (this.isOffline()) {
            return this.saveDrawingOffline(
                finalImageData,
                elements,
                appState,
                files,
                question,
                drawingConfig,
                handleUserResponse
            );
        }

        const state = storeManager.getState();
        const context = state?.session?.context;
        const isPreviewContext = context === sessionConstants.CONTEXT_PREVIEW || context === sessionConstants.CONTEXT_PREVIEW_DRAFT;
        const shouldSkipS3Upload = isPreviewContext || !services?.uploadService;

        if (shouldSkipS3Upload) {
            return this.saveDrawingLocally(
                excalidrawRef,
                question,
                drawingConfig,
                isDarkModeEnabled,
                handleUserResponse
            );
        }

        const contentRevisionId = question.contentRevisionId;
        const responseIdentifier = drawingConfig?.responseIdentifier;
        const fileName = `${contentRevisionId}-${responseIdentifier}.png`;
        const jsonFileName = `${contentRevisionId}-${responseIdentifier}.json`;

        const { blob: imageBlob, mimeType } = this.createImageBlob(finalImageData, fileName);
        const scene = { elements, appState, files };
        const jsonBlob = this.createJsonBlob(scene, jsonFileName);

        const skipContentUrlCreation = settingsReader.shouldStoreDrawingsAsUri();

        return new Promise((resolve, reject) => {
            const uploadState = {
                imageContentItemId: null,
                jsonContentItemId: null,
                imageS3Uri: null,
                jsonS3Uri: null,
                imageUploaded: false,
                jsonUploaded: false,
            };

            const onBothUploaded = () => {
                this.handleBothUploadsComplete({
                    uploadState,
                    skipContentUrlCreation,
                    services,
                    sceneData: { scene, elements },
                    drawingConfig,
                    fileInfo: { fileName, fileType: mimeType, imageBlob, finalImageData },
                    contentRevisionId,
                    handleUserResponse,
                    resolve,
                });
            };

            const uploadCallback = this.createUploadCallback({
                skipContentUrlCreation,
                fileNames: {
                    imageFileName: fileName,
                    jsonFileName,
                    imageBlobName: imageBlob.name,
                    jsonBlobName: jsonBlob.name,
                },
                state: uploadState,
                onBothUploaded,
                onError: reject,
            });

            services.uploadService.upload(imageBlob, question.contentItemId, question.contentRevisionId, uploadCallback, skipContentUrlCreation);
            services.uploadService.upload(jsonBlob, question.contentItemId, question.contentRevisionId, uploadCallback, skipContentUrlCreation);
        });
    }

    static async checkAndAutoSave({
        excalidrawRef,
        showExcalidraw,
        services,
        question,
        drawingConfig,
        isDarkModeEnabled,
        handleUserResponse,
        isEdited,
        lastSaveTime,
        autoSaveFrequency,
        isSaving,
        isSavingLocally,
    }) {
        if (!showExcalidraw || !excalidrawRef?.current || !services || !question) {
            return { shouldSave: false, reason: 'missing_requirements' };
        }

        if (!excalidrawRef.current.getSceneElements || !excalidrawRef.current.getAppState) {
            return { shouldSave: false, reason: 'excalidraw_not_ready' };
        }

        if (this.isOffline()) {
            return { shouldSave: false, reason: 'offline' };
        }

        const now = Date.now();
        const timeSinceLastSave = lastSaveTime ? now - lastSaveTime : Infinity;
        const requiredTime = autoSaveFrequency * 1000;

        if (timeSinceLastSave < requiredTime) {
            return { shouldSave: false, reason: 'too_soon' };
        }

        if (!isEdited || isSaving || isSavingLocally) {
            return { shouldSave: false, reason: 'not_edited_or_saving' };
        }

        try {
            const result = await this.saveDrawingToServer(
                excalidrawRef,
                services,
                question,
                drawingConfig,
                isDarkModeEnabled,
                handleUserResponse
            );

            if (result) {
                return {
                    shouldSave: true,
                    result,
                    snapshot: this.captureSnapshot(excalidrawRef),
                };
            }

            return { shouldSave: false, reason: 'no_result' };
        } catch (err) {
            return { shouldSave: false, reason: 'error', error: err };
        }
    }

    static shouldApplyDarkMode(contrastOption) {
        return contrastOption === 'yellow-on-black' || contrastOption === 'inverted';
    }

    static getExcalidrawTheme(isDarkModeEnabled) {
        return isDarkModeEnabled ? 'dark' : 'light';
    }

    static isOffline() {
        return !navigator.onLine || !utilService.isBrowserStatusOnline();
    }


    static extractContentItemId(payload) {
        if (!payload) {
            return null;
        }
        return payload.contentItemId || payload.contentRevisionId || payload;
    }

    static handleBothUploadsComplete(config) {
        const {
            uploadState,
            skipContentUrlCreation,
            services,
            sceneData = {},
            drawingConfig,
            fileInfo = {},
            contentRevisionId,
            handleUserResponse,
            resolve,
        } = config;

        const { scene = null, sceneJson = null, elements = null } = sceneData;
        const { fileName = '', fileType = '', imageBlob = null, finalImageData = null } = fileInfo;

        const hasS3Uris = uploadState.imageS3Uri && uploadState.jsonS3Uri;
        const hasContentItemIds = uploadState.imageContentItemId && uploadState.jsonContentItemId;
        const isReady = skipContentUrlCreation ? hasS3Uris : hasContentItemIds;

        if (isReady) {
            const imageUrl = skipContentUrlCreation
                ? uploadState.imageS3Uri
                : services.uploadService.getFileDownloadUrl(uploadState.imageContentItemId);

            const jsonData = sceneJson || JSON.stringify(scene);
            const drawingExtraResponses = this.createExtraResponses(
                drawingConfig?.responseIdentifier || drawingConfig.responseIdentifier,
                {
                    imageId: uploadState.imageContentItemId || '',
                    jsonId: uploadState.jsonContentItemId || '',
                    fileName,
                    fileType,
                    fileSize: String(imageBlob?.size || 0),
                },
                {
                    offlineData: finalImageData ? { imageBase64: finalImageData, sceneJson: jsonData } : { imageBase64: null, sceneJson: jsonData },
                    imageS3Uri: uploadState.imageS3Uri,
                    jsonS3Uri: uploadState.jsonS3Uri,
                }
            );

            const interactionIdentifier = drawingConfig?.responseIdentifier || drawingConfig.responseIdentifier;
            const mainResponseId = uploadState.imageContentItemId || '';
            handleUserResponse(
                contentRevisionId,
                interactionIdentifier,
                [String(mainResponseId)],
                drawingExtraResponses
            );

            const result = {
                imageUrl,
                imageContentItemId: uploadState.imageContentItemId,
                jsonContentItemId: uploadState.jsonContentItemId,
            };
            if (elements) {
                result.elements = elements;
            }
            resolve(result);
        }
    }

    static parseExistingDrawingData(existingBase64, existingJsonData) {
        if (!existingBase64 || !existingJsonData) {
            return null;
        }
        try {
            const scene = JSON.parse(existingJsonData);
            return {
                imageUrl: existingBase64,
                elements: scene.elements,
                appState: scene.appState,
                files: scene.files,
                isOffline: false,
            };
        } catch (err) {
            console.error('Failed to parse existing drawing data:', err);
            return null;
        }
    }

    static async fetchAndConvertImageToBase64(imageResponse) {
        const imageBlob = await imageResponse.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(imageBlob);
        });
    }

    static async fetchAndProcessDrawingData(imageResponse, jsonResponse, existingBase64, existingJsonData) {
        if (!imageResponse.ok || !jsonResponse.ok) {
            const fallbackData = this.parseExistingDrawingData(existingBase64, existingJsonData);
            return fallbackData || null;
        }

        const imageBase64 = await this.fetchAndConvertImageToBase64(imageResponse);
        const sceneJson = await jsonResponse.text();
        const scene = JSON.parse(sceneJson);

        return {
            imageBase64,
            sceneJson,
            scene,
        };
    }

    static createDrawingExtraResponses(responseIdentifier, imageBase64, sceneJson) {
        return [
            {
                identifier: `${responseIdentifier}_drawingBase64`,
                candidateResponse: [imageBase64],
            },
            {
                identifier: `${responseIdentifier}_jsonData`,
                candidateResponse: [sceneJson],
            },
            {
                identifier: `${responseIdentifier}_pendingSync`,
                candidateResponse: ['false'],
            },
        ];
    }

    static storeDrawingDataInXML(question, drawingConfig, handleUserResponse, userResponses, imageBase64, sceneJson, mainResponseId = '') {
        const extraResponses = this.createDrawingExtraResponses(
            drawingConfig.responseIdentifier,
            imageBase64,
            sceneJson
        );
        handleUserResponse(
            question.contentRevisionId,
            drawingConfig.responseIdentifier,
            [mainResponseId || userResponses?.[drawingConfig.responseIdentifier]?.[0] || ''],
            extraResponses
        );
    }

    static createUploadCallback(config) {
        const { skipContentUrlCreation, fileNames, state, onBothUploaded, onError } = config;

        return (type, file, payload) => {
            if (type === 'sent-upload-success') {
                this.handleUploadSuccess(type, file, payload, skipContentUrlCreation, fileNames, state);
                const bothReady = skipContentUrlCreation
                    ? (state.imageUploaded && state.jsonUploaded && state.imageS3Uri && state.jsonS3Uri)
                    : (state.imageUploaded && state.jsonUploaded);

                if (bothReady) {
                    onBothUploaded();
                }
            } else if (type === 'sent-upload-error') {
                onError(new Error('Upload failed'));
            }
        };
    }

    static handleUploadSuccess(type, file, payload, skipContentUrlCreation, fileNames, state) {
        const { imageFileName, jsonFileName, imageBlobName, jsonBlobName } = fileNames;

        if (skipContentUrlCreation) {
            const s3Url = payload?.s3Url;
            if (s3Url) {
                const isImage = file.name && (file.name.endsWith('.png') || file.name === imageFileName || file.name === imageBlobName);
                const isJson = file.name && (file.name.endsWith('.json') || file.name === jsonFileName || file.name === jsonBlobName);
                if (isImage && !state.imageUploaded) {
                    state.imageS3Uri = s3Url;
                    state.imageUploaded = true;
                } else if (isJson && !state.jsonUploaded) {
                    state.jsonS3Uri = s3Url;
                    state.jsonUploaded = true;
                }
            }
        } else {
            const contentItemId = this.extractContentItemId(payload);
            if (file.name === imageFileName || file.name === imageBlobName) {
                state.imageContentItemId = contentItemId;
                state.imageUploaded = true;
            } else {
                state.jsonContentItemId = contentItemId;
                state.jsonUploaded = true;
            }
        }
    }


    static extractS3UrisFromResponses(questionResponse, responseIdentifier) {
        if (!questionResponse?.responseIdentifiers) {
            return { imageS3Uri: null, jsonS3Uri: null };
        }

        const fileUriResponse = questionResponse.responseIdentifiers.find(
            r => r.identifier === `${responseIdentifier}_fileURI`
        );
        const jsonDataUriResponse = questionResponse.responseIdentifiers.find(
            r => r.identifier === `${responseIdentifier}_jsonDataURI`
        );

        const imageS3Uri = fileUriResponse?.userResponse?.[0] || null;
        const jsonS3Uri = jsonDataUriResponse?.userResponse?.[0] || null;

        return {
            imageS3Uri,
            jsonS3Uri,
        };
    }


    static resolvePresignedUrls(questionResponse, responseIdentifier) {
        if (!questionResponse?.presignedUrls) {
            return { imagePresignedUrl: null, jsonPresignedUrl: null };
        }

        const { imageS3Uri, jsonS3Uri } = this.extractS3UrisFromResponses(
            questionResponse,
            responseIdentifier
        );

        const presignedUrls = questionResponse.presignedUrls;
        const imagePresignedUrl = imageS3Uri ? presignedUrls[imageS3Uri] || null : null;
        const jsonPresignedUrl = jsonS3Uri ? presignedUrls[jsonS3Uri] || null : null;

        return {
            imagePresignedUrl,
            jsonPresignedUrl,
        };
    }


    static async loadDrawingDataFromPresignedUrl(presignedUrl) {
        if (!presignedUrl) {
            return null;
        }

        try {
            const response = await fetch(presignedUrl);
            if (!response.ok) {
                return null;
            }
            const data = await response.json();
            return data.elements || data;
        } catch (err) {
            console.error('Failed to load drawing data from presigned URL:', err);
            return null;
        }
    }


    static async tryPreloadDrawingWithPresignedUrls(question, drawingConfig, userResponses, handleUserResponse, forceFetch = false) {
        if (!question || !drawingConfig) {
            return null;
        }

        if (!settingsReader.shouldStoreDrawingsAsUri()) {
            return null;
        }

        const state = storeManager.getState();
        const questionResponse = state.response[question.contentRevisionId];
        if (!questionResponse) {
            return null;
        }

        const { imagePresignedUrl, jsonPresignedUrl } = this.resolvePresignedUrls(
            questionResponse,
            drawingConfig.responseIdentifier
        );

        if (!imagePresignedUrl || !jsonPresignedUrl) {
            return null;
        }

        return this.preloadDrawingWithPresignedUrls(
            imagePresignedUrl,
            jsonPresignedUrl,
            question,
            drawingConfig,
            handleUserResponse,
            userResponses,
            forceFetch
        );
    }

    static resolveDrawingUrls(question, drawingConfig, userResponses, services) {
        const imageId = userResponses?.[drawingConfig?.responseIdentifier]?.[0];
        const jsonId = userResponses?.[`${drawingConfig?.responseIdentifier}_jsonId`]?.[0];

        let imageUrl = null;
        let jsonUrl = null;
        let usePresignedUrls = false;

        if (settingsReader.shouldStoreDrawingsAsUri() && question) {
            const state = storeManager.getState();
            const questionResponse = state.response[question.contentRevisionId];
            if (questionResponse) {
                const { imagePresignedUrl, jsonPresignedUrl } = this.resolvePresignedUrls(
                    questionResponse,
                    drawingConfig?.responseIdentifier
                );

                if (imagePresignedUrl) {
                    imageUrl = imagePresignedUrl;
                    usePresignedUrls = true;
                }

                if (jsonPresignedUrl) {
                    jsonUrl = jsonPresignedUrl;
                    usePresignedUrls = true;
                }
            }
        }


        if (!imageUrl && imageId && typeof imageId === 'string' && imageId.trim() !== '' && services?.uploadService) {
            imageUrl = services.uploadService.getFileDownloadUrl(imageId);
        }

        if (!jsonUrl && jsonId && typeof jsonId === 'string' && jsonId.trim() !== '' && services?.uploadService) {
            jsonUrl = services.uploadService.getFileDownloadUrl(jsonId);
        }

        return { imageUrl, jsonUrl, usePresignedUrls };
    }

    static async saveDrawingLocally(
        excalidrawRef,
        question,
        drawingConfig,
        isDarkModeEnabled,
        handleUserResponse
    ) {
        if (!excalidrawRef.current || !question) {
            return null;
        }

        const exportResult = await this.exportDrawingToImage(excalidrawRef);
        if (!exportResult) {
            return null;
        }

        const { finalImageData, elements, appState, files } = exportResult;
        const scene = { elements, appState, files };
        const sceneJson = JSON.stringify(scene);

        const extraResponses = this.createOfflineExtraResponses(
            drawingConfig.responseIdentifier,
            finalImageData,
            sceneJson
        );

        handleUserResponse(
            question.contentRevisionId,
            drawingConfig.responseIdentifier,
            [''],
            extraResponses
        );

        return {
            imageUrl: finalImageData,
            elements,
            isLocalOnly: true,
        };
    }

    static async saveDrawingOffline(imageData, elements, appState, files, question, drawingConfig, handleUserResponse) {
        const scene = { elements, appState, files };
        const sceneJson = JSON.stringify(scene);

        const extraResponses = this.createOfflineExtraResponses(
            drawingConfig.responseIdentifier,
            imageData,
            sceneJson
        );

        handleUserResponse(
            question.contentRevisionId,
            drawingConfig.responseIdentifier,
            [''],
            extraResponses
        );

        return {
            imageUrl: imageData,
            elements,
            isOffline: true,
        };
    }

    static loadOfflineDrawingData(userResponses, drawingConfig, services = null) {
        if (!drawingConfig?.responseIdentifier) {
            return null;
        }

        const base64Key = `${drawingConfig.responseIdentifier}_drawingBase64`;
        const jsonKey = `${drawingConfig.responseIdentifier}_jsonData`;
        const pendingSyncKey = `${drawingConfig.responseIdentifier}_pendingSync`;

        const imageBase64 = userResponses?.[base64Key]?.[0];
        const sceneJson = userResponses?.[jsonKey]?.[0];
        const pendingSync = userResponses?.[pendingSyncKey]?.[0] === 'true';

        if (imageBase64 && sceneJson) {
            try {
                const scene = JSON.parse(sceneJson);
                return {
                    imageUrl: imageBase64,
                    elements: scene.elements,
                    appState: scene.appState,
                    files: scene.files,
                    isOffline: pendingSync || this.isOffline(),
                };
            } catch (err) {
                console.error('Failed to parse scene JSON:', err);
                return null;
            }
        }

        if (this.isOffline()) {
            return null;
        }

        const imageId = userResponses?.[drawingConfig.responseIdentifier]?.[0];
        if (imageId && services?.uploadService && sceneJson) {
            try {
                const scene = JSON.parse(sceneJson);
                return {
                    imageUrl: services.uploadService.getFileDownloadUrl(imageId),
                    elements: scene.elements,
                    appState: scene.appState,
                    files: scene.files,
                    isOffline: false,
                };
            } catch (err) {
                console.error('Failed to parse scene JSON:', err);
                return null;
            }
        }

        return null;
    }


    static async preloadDrawingWithPresignedUrls(
        imagePresignedUrl,
        jsonPresignedUrl,
        question,
        drawingConfig,
        handleUserResponse,
        userResponses,
        forceFetch = false
    ) {
        if (!drawingConfig?.responseIdentifier) {
            return null;
        }

        if (this.isOffline()) {
            return null;
        }

        const existingBase64 = userResponses?.[`${drawingConfig?.responseIdentifier}_drawingBase64`]?.[0];
        const existingJsonData = userResponses?.[`${drawingConfig?.responseIdentifier}_jsonData`]?.[0];

        if (!forceFetch) {
            const existingData = this.parseExistingDrawingData(existingBase64, existingJsonData);
            if (existingData) {
                return existingData;
            }
        }

        try {
            const [imageResponse, jsonResponse] = await Promise.all([
                fetch(imagePresignedUrl),
                fetch(jsonPresignedUrl),
            ]);

            const processed = await this.fetchAndProcessDrawingData(
                imageResponse,
                jsonResponse,
                existingBase64,
                existingJsonData
            );
            if (!processed) {
                return null;
            }

            this.storeDrawingDataInXML(
                question,
                drawingConfig,
                handleUserResponse,
                userResponses,
                processed.imageBase64,
                processed.sceneJson
            );

            return {
                imageUrl: processed.imageBase64,
                elements: processed.scene.elements,
                appState: processed.scene.appState,
                files: processed.scene.files,
                isOffline: false,
            };
        } catch (err) {
            console.error('Failed to preload drawing with presigned URLs:', err);
            return null;
        }
    }

    static async loadAndStoreS3Data(services, question, drawingConfig, handleUserResponse, userResponses) {
        if (!drawingConfig?.responseIdentifier) {
            return null;
        }

        const imageId = userResponses?.[drawingConfig?.responseIdentifier]?.[0];
        const jsonId = userResponses?.[`${drawingConfig?.responseIdentifier}_jsonId`]?.[0];

        if (!imageId || !jsonId) {
            return null;
        }

        // Only fetch from S3 when online
        if (this.isOffline()) {
            return null;
        }

        const existingBase64 = userResponses?.[`${drawingConfig?.responseIdentifier}_drawingBase64`]?.[0];
        const existingJsonData = userResponses?.[`${drawingConfig?.responseIdentifier}_jsonData`]?.[0];

        const existingData = this.parseExistingDrawingData(existingBase64, existingJsonData);
        if (existingData) {
            return existingData;
        }

        try {
            const imageUrl = services.uploadService.getFileDownloadUrl(imageId);
            const jsonUrl = services.uploadService.getFileDownloadUrl(jsonId);

            const [imageResponse, jsonResponse] = await Promise.all([
                fetch(imageUrl),
                fetch(jsonUrl),
            ]);

            const processed = await this.fetchAndProcessDrawingData(
                imageResponse,
                jsonResponse,
                null,
                null
            );
            if (!processed) {
                return null;
            }

            this.storeDrawingDataInXML(
                question,
                drawingConfig,
                handleUserResponse,
                userResponses,
                processed.imageBase64,
                processed.sceneJson,
                imageId
            );

            return {
                imageUrl: processed.imageBase64,
                elements: processed.scene.elements,
                appState: processed.scene.appState,
                files: processed.scene.files,
                isOffline: false,
            };
        } catch (err) {
            console.error('Failed to load and store S3 data:', err);
            return null;
        }
    }

    static async retryOfflineUploads(services, question, drawingConfig, questionResponse) {
        if (this.isOffline()) {
            return;
        }

        const pendingSyncResponse = questionResponse?.responseIdentifiers?.find(
            r => r.identifier === `${drawingConfig.responseIdentifier}_pendingSync`
        );
        const imageBase64Response = questionResponse?.responseIdentifiers?.find(
            r => r.identifier === `${drawingConfig.responseIdentifier}_drawingBase64`
        );
        const sceneJsonResponse = questionResponse?.responseIdentifiers?.find(
            r => r.identifier === `${drawingConfig.responseIdentifier}_jsonData`
        );

        const pendingSync = this.isPendingSyncTrue(pendingSyncResponse);

        if (!pendingSync) {
            return;
        }

        const imageBase64 = imageBase64Response?.userResponse?.[0];
        const sceneJson = sceneJsonResponse?.userResponse?.[0];

        if (!imageBase64 || !sceneJson) {
            return;
        }

        // Use question.contentRevisionId - this is what we stored when saving offline
        const contentRevisionId = question.contentRevisionId;

        const { blob: imageBlob } = this.createImageBlob(imageBase64, `${contentRevisionId}-${drawingConfig.responseIdentifier}.png`);
        let scene;
        try {
            scene = JSON.parse(sceneJson);
        } catch (err) {
            console.error('Failed to parse scene JSON for retry:', err);
            return;
        }
        const jsonBlob = this.createJsonBlob(scene, `${contentRevisionId}-${drawingConfig.responseIdentifier}.json`);

        await this.uploadBlobsToS3(
            imageBlob,
            jsonBlob,
            services,
            question,
            drawingConfig,
            sceneJson,
            contentRevisionId
        );
    }

    static async uploadBlobsToS3(imageBlob, jsonBlob, services, question, drawingConfig, sceneJson = null, contentRevisionId = null) {
        const skipContentUrlCreation = settingsReader.shouldStoreDrawingsAsUri();

        return new Promise((resolve, reject) => {
            const uploadState = {
                imageContentItemId: null,
                jsonContentItemId: null,
                imageS3Uri: null,
                jsonS3Uri: null,
                imageUploaded: false,
                jsonUploaded: false,
            };

            const onBothUploaded = () => {
                const revisionId = contentRevisionId || question.contentRevisionId;
                this.handleBothUploadsComplete({
                    uploadState,
                    skipContentUrlCreation,
                    services,
                    sceneData: { sceneJson },
                    drawingConfig,
                    fileInfo: { fileName: imageBlob.name, fileType: imageBlob.type, imageBlob },
                    contentRevisionId: revisionId,
                    handleUserResponse: responseService.handleUserResponse.bind(responseService),
                    resolve,
                });
            };

            const uploadCallback = this.createUploadCallback({
                skipContentUrlCreation,
                fileNames: {
                    imageFileName: imageBlob.name,
                    jsonFileName: jsonBlob.name,
                    imageBlobName: imageBlob.name,
                    jsonBlobName: jsonBlob.name,
                },
                state: uploadState,
                onBothUploaded,
                onError: reject,
            });

            services.uploadService.upload(imageBlob, question.contentItemId, question.contentRevisionId, uploadCallback, skipContentUrlCreation);
            services.uploadService.upload(jsonBlob, question.contentItemId, question.contentRevisionId, uploadCallback, skipContentUrlCreation);
        });
    }

    static cleanupOfflineData(contentRevisionId, responseIdentifier) {
        const cleanupResponses = [
            {
                identifier: `${responseIdentifier}_drawingBase64`,
                candidateResponse: [],
            },
            {
                identifier: `${responseIdentifier}_jsonData`,
                candidateResponse: [],
            },
            {
                identifier: `${responseIdentifier}_pendingSync`,
                candidateResponse: [],
            },
        ];

        for (const cleanupResponse of cleanupResponses) {
            responseService.handleUserResponse(contentRevisionId, cleanupResponse.identifier, [], []);
        }
    }

    static createDeleteDrawingExtraResponses(responseIdentifier) {
        // Clean up all drawing-related extra response identifiers, including offline fields
        const drawingExtraIdentifiers = this.getDrawingExtraIdentifiers(responseIdentifier);
        return drawingExtraIdentifiers.map(identifier => ({
            identifier,
            candidateResponse: [],
        }));
    }

    static findDrawingResponseIdentifier(questionResponse, question) {
        let drawingResponseIdentifier = null;
        let jsonIdResponse = null;

        for (const response of questionResponse.responseIdentifiers) {
            if (response.identifier.endsWith('_fileURI') && response.userResponse?.[0]) {
                drawingResponseIdentifier = response.identifier.replace('_fileURI', '');
                break;
            }
            if (response.identifier.endsWith('_jsonDataURI') && response.userResponse?.[0]) {
                drawingResponseIdentifier = response.identifier.replace('_jsonDataURI', '');
                break;
            }
            if (response.identifier.endsWith('_jsonId') && response.userResponse?.[0]) {
                drawingResponseIdentifier = response.identifier.replace('_jsonId', '');
                jsonIdResponse = response;
                break;
            }
        }

        if (!drawingResponseIdentifier) {
            const drawingConfig = DrawingCanvasService.getDrawingConfig(question);
            if (drawingConfig?.enabled && drawingConfig?.responseIdentifier) {
                drawingResponseIdentifier = drawingConfig.responseIdentifier;
                jsonIdResponse = questionResponse.responseIdentifiers.find(
                    r => r.identifier === `${drawingConfig.responseIdentifier}_jsonId`
                );
            }
        }

        return { drawingResponseIdentifier, jsonIdResponse };
    }

    static preloadDrawingForQuestion(contentRevisionId, responses, services) {
        const question = assessmentReader.getQuestion(contentRevisionId);
        if (!question) {
            return null;
        }

        const questionResponse = responses[contentRevisionId];
        if (!questionResponse?.responseIdentifiers) {
            return null;
        }

        const { drawingResponseIdentifier, jsonIdResponse } = this.findDrawingResponseIdentifier(questionResponse, question);
        if (!drawingResponseIdentifier) {
            return null;
        }

        const mainResponse = questionResponse.responseIdentifiers.find(
            r => r.identifier === drawingResponseIdentifier
        );

        const imageId = mainResponse?.userResponse?.[0];
        const jsonId = jsonIdResponse?.userResponse?.[0];

        const drawingConfigForPreload = {
            enabled: true,
            responseIdentifier: drawingResponseIdentifier,
        };

        const userResponsesObj = {};
        for (const r of questionResponse.responseIdentifiers) {
            userResponsesObj[r.identifier] = r.userResponse;
        }

        if (settingsReader.shouldStoreDrawingsAsUri()) {
            const presignedPromise = this.tryPreloadWithPresignedUrls(
                questionResponse,
                drawingResponseIdentifier,
                question,
                drawingConfigForPreload,
                userResponsesObj
            );
            if (presignedPromise !== null) {
                return presignedPromise;
            }
        }

        if (!imageId || !jsonId) {
            return null;
        }

        if (this.hasExistingOfflineData(questionResponse, drawingResponseIdentifier)) {
            return null;
        }

        return this.loadAndStoreS3Data(
            services,
            question,
            drawingConfigForPreload,
            responseService.handleUserResponse.bind(responseService),
            userResponsesObj
        );
    }

    static tryPreloadWithPresignedUrls(questionResponse, drawingResponseIdentifier, question, drawingConfig, userResponsesObj) {
        const { imagePresignedUrl, jsonPresignedUrl } = this.resolvePresignedUrls(
            questionResponse,
            drawingResponseIdentifier
        );

        if (imagePresignedUrl && jsonPresignedUrl) {
            return this.preloadDrawingWithPresignedUrls(
                imagePresignedUrl,
                jsonPresignedUrl,
                question,
                drawingConfig,
                responseService.handleUserResponse.bind(responseService),
                userResponsesObj,
                false
            );
        }
        return null;
    }

    static hasExistingOfflineData(questionResponse, drawingResponseIdentifier) {
        const existingBase64 = questionResponse.responseIdentifiers.find(
            r => r.identifier === `${drawingResponseIdentifier}_drawingBase64`
        );
        const existingJsonData = questionResponse.responseIdentifiers.find(
            r => r.identifier === `${drawingResponseIdentifier}_jsonData`
        );
        return !!(existingBase64?.userResponse?.[0] && existingJsonData?.userResponse?.[0]);
    }

    static async preloadAllDrawingsForOffline(assessment = null) {
        if (this.isOffline() || !settingsReader.isCandidateDrawingOptionEnabled()) {
            return;
        }

        const services = questionHelperServices.getServices();
        const assessmentToUse = assessment || assessmentReader.getAssessment();

        if (!services?.uploadService || !assessmentToUse?.sections) {
            return;
        }

        if (!excalidrawPreloadService.hasDrawingInteractions(assessmentToUse)) {
            return;
        }

        const state = storeManager.getState();
        const responses = state.response;

        const allQuestionIds = assessmentToUse.sections.flatMap(section =>
            (section.questions || []).map(question => question.contentRevisionId)
        );

        const preloadPromises = allQuestionIds
            .map(contentRevisionId => this.preloadDrawingForQuestion(contentRevisionId, responses, services))
            .filter(promise => promise !== null);

        await Promise.all(preloadPromises);
    }

    static clearPendingSyncForAllDrawings() {
        if (!settingsReader.isCandidateDrawingOptionEnabled()) {
            return;
        }

        const state = storeManager.getState();
        const responses = state.response;

        for (const contentRevisionIdKey of Object.keys(responses)) {
            const contentRevisionId = Number.parseInt(contentRevisionIdKey, 10);
            const questionResponse = responses[contentRevisionIdKey];
            const identifiers = questionResponse?.responseIdentifiers;

            if (Array.isArray(identifiers)) {
                identifiers.forEach((response) => {
                    const baseResponseId = response.identifier.replace('_pendingSync', '');
                    if (this.isPendingSyncTrue(response) && this.isDrawingResponseIdentifier(baseResponseId)) {
                        responseService.handleUserResponse(
                            contentRevisionId,
                            response.identifier,
                            ['false'],
                            []
                        );
                    }
                });
            }
        }
    }

    static async retryAllOfflineDrawings() {
        if (this.isOffline() || !settingsReader.isCandidateDrawingOptionEnabled()) {
            return;
        }

        const services = questionHelperServices.getServices();

        const state = storeManager.getState();
        const responses = state.response;

        const offlineDrawings = this.collectPendingDrawingResponses(responses);

        const uploadPromises = offlineDrawings
            .map((drawing) => {
                const question = assessmentReader.getQuestion(drawing.contentRevisionId);
                if (!question) {
                    return null;
                }

                const baseConfig = DrawingCanvasService.getDrawingConfig(question);
                if (!baseConfig.enabled) {
                    return null;
                }

                const drawingConfig = { ...baseConfig, responseIdentifier: drawing.responseIdentifier };

                const questionResponse = responses[drawing.contentRevisionId];

                return DrawingCanvasService.retryOfflineUploads(
                    services,
                    question,
                    drawingConfig,
                    questionResponse
                );
            })
            .filter(promise => promise !== null);

        await Promise.all(uploadPromises);
        this.clearPendingSyncForAllDrawings();
    }

    static collectPendingDrawingResponses(responses) {
        const pending = [];
        Object.keys(responses).forEach((contentRevisionIdKey) => {
            const questionResponse = responses[contentRevisionIdKey];
            const identifiers = questionResponse?.responseIdentifiers;

            if (Array.isArray(identifiers)) {
                identifiers.forEach((response) => {
                    if (!this.isPendingSyncTrue(response)) {
                        return;
                    }
                    const baseResponseId = response.identifier.replace('_pendingSync', '');
                    pending.push({
                        contentRevisionId: Number.parseInt(contentRevisionIdKey, 10),
                        responseIdentifier: baseResponseId,
                    });
                });
            }
        });
        return pending;
    }

    static isPendingSyncTrue(response) {
        if (!response?.identifier?.endsWith('_pendingSync')) {
            return false;
        }
        const pendingValue = response.userResponse?.[0];
        if (pendingValue === undefined || pendingValue === null) {
            return true;
        }
        return pendingValue === 'true' || pendingValue === true;
    }

    static getExcalidrawLanguageCode(currentLanguage) {
        const excalidrawLanguages = excalidrawPreloadService.getLanguages();
        const defaultLang = excalidrawPreloadService.getDefaultLang();

        if (!excalidrawLanguages || !defaultLang) {
            return 'en';
        }

        const supportedCodes = excalidrawLanguages.map(lang => lang.code);
        const lowerCurrentLanguage = (currentLanguage || 'en-US').toLowerCase();

        const exactMatch = supportedCodes.find(code => code.toLowerCase() === lowerCurrentLanguage);
        if (exactMatch) {
            return exactMatch;
        }

        const baseCode = lowerCurrentLanguage.split('-')[0];
        const baseMatch = supportedCodes.find((code) => {
            const lower = code.toLowerCase();
            return lower === baseCode || lower.startsWith(`${baseCode}-`);
        });
        if (baseMatch) {
            return baseMatch;
        }

        return defaultLang.code;
    }
}

export default DrawingCanvasService;
